import { Component, OnDestroy, OnInit, input, output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EkBreadcrumbComponent } from '@beta-foundations/ui-kit/menu/breadcrumb';
import { MenuItem } from 'primeng/api';
import { LogoComponent } from '../logo/logo.component';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { NavbarComponent } from '../navbar/navbar.component';
import { EkButtonComponent } from "@beta-foundations/ui-kit/buttons";
import { MenuComponent } from "../menu/menu.component";
import { NavSubmenuComponent } from '../sub-menu/sub-menu.component';
import { combineLatest, Observable, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { TabNames } from '../../models/enumerate/tab-names.enum';
import { AuthService } from '../../../services/auth.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InsegnamentoLegendaModalComponent } from '../insegnamento-legenda-modal/insegnamento-legenda-modal.component';
import { CambioInsegnamentoComponent } from '../cambio-insegnamento-modal/cambio-insegnamento-modal.component';
import { PageTitleService } from '../../services/page-title.service';
import { InsegnamentoService } from '../../services/insegnamento.service';
import { NavSubmenuService } from '../../services/submenu.service';
import { SettingsService } from '../../services/settings.service';
import { UserService } from '../../services/user.service';
import { User, UserGroup } from '../../models/user';
import { ChipModule } from 'primeng/chip';

@Component({
    selector: 'app-custom-header',
    standalone: true,
    imports: [
        TranslateModule,
        NavbarComponent,
        CommonModule,
        EkBreadcrumbComponent,
        LogoComponent,
        EkButtonComponent,
        MenuComponent,
        NavSubmenuComponent,
        ChipModule
    ],
    templateUrl: './custom-header.component.html',
    styleUrl: './custom-header.component.scss'
})
export class CustomHeaderComponent implements OnInit, OnDestroy {
    hideSubmenu = input(false);
    showFeedbackButton = input(false);
    feedbackClick = output<void>();

    home: MenuItem | undefined = { label: 'Home', url: '/overview', disabled: true };
    items: MenuItem[] = [];
    pageTitle = '';
    pageSubtitle = '';
    headerTitle: string = '';
    isMenuOpen: boolean = false;
    ref: DynamicDialogRef | undefined;
    isSubmenuVisible = false;
    examStatus: 'UNSAVED' | 'BOZZA' | 'COMPLETATO' | null = null;
    showExamStatus: boolean = false;

    public showSubmenu: boolean = true;
    private readonly excludedRoute = '/home'; 

    private destroy$ = new Subject<void>();
    public hasSelectedTeaching$!: Observable<boolean>;
    public canOpenChangeModal$!: Observable<boolean>;
    public isExamEditOrCreateMode$!: Observable<boolean>;

    constructor(
        private route: ActivatedRoute,
        private translate: TranslateService,
        private router: Router,
        private dialogService: DialogService,
        private authService: AuthService,
        private insegnamentoService: InsegnamentoService,
        private submenuService: NavSubmenuService,
        private pageTitleService: PageTitleService,
        private settingsService: SettingsService,
        private userService: UserService
    ) {
    }

    ngOnInit(): void {
        this.hasSelectedTeaching$ = this.insegnamentoService.selectedTeaching$.pipe(
            map(teaching => teaching !== null)
        );
        const canManageSubtenants$ = combineLatest([
            this.settingsService.isFeatureEnabled('subtenant', 'PERMISSIONS'),
            this.userService.getUser()
        ]).pipe(
            map(([enabled, user]) => enabled && this.isSubtenantManager(user))
        );

        this.canOpenChangeModal$ = combineLatest([
            this.insegnamentoService.teachings$,
            canManageSubtenants$
        ]).pipe(
            map(([teachings, canManage]) => {
                const count = teachings?.length ?? 0;
                return count > 1 || (count >= 1 && canManage);
            })
        );

        this.isExamEditOrCreateMode$ = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.router.url),
            startWith(this.router.url),
            map(url => {
                return /\/examples\/(manual-creation|\d+\/edit)/.test(url)
                    || /\/tests\/(manual-creation|\d+\/edit)/.test(url);
            })
        );


        this.submenuService.config$
            .pipe(takeUntil(this.destroy$))
            .subscribe(config => {
                this.isSubmenuVisible = config !== null && config.visible !== false;
            });

        this.checkSubmenuVisibility();
        this.setCurrentBreadcrumbOnReload();
        this.authService.getUserInfo().subscribe(user => {
            if (user) {
                this.headerTitle = user.name + " " + user.surname;
            }
        });

        this.pageTitleService.examStatus$
            .pipe(takeUntil(this.destroy$))
            .subscribe(status => {
                this.examStatus = status;
                this.showExamStatus = status !== null;
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                tap(() => this.checkSubmenuVisibility()),
                map(() => this.route),
                map((route) => {
                    while (route.firstChild) route = route.firstChild;
                    return route.snapshot;
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((snapshot) => {
                this.updateBreadcrumbFromRouteSnapshot(snapshot);
            });

        this.pageTitleService.pageTitle$
            .pipe(takeUntil(this.destroy$))
            .subscribe((dynamicTitle) => {
                if (dynamicTitle) {
                    this.pageTitle = dynamicTitle;
                    this.setBreadcrumb(dynamicTitle);
                }
            });

        this.pageTitleService.pageSubtitle$
            .pipe(takeUntil(this.destroy$))
            .subscribe((dynamicSubtitle) => {
                this.pageSubtitle = dynamicSubtitle || '';
            });

        this.pageTitleService.customBreadcrumbs$
            .pipe(takeUntil(this.destroy$))
            .subscribe((customBreadcrumbs) => {
                if (customBreadcrumbs) {
                    this.items = customBreadcrumbs;
                }
            });
    }

    private isSubtenantManager(user: User | null): boolean {
        return user?.group === UserGroup.SYS_ADMIN || user?.group === UserGroup.ADMIN;
    }

    private checkSubmenuVisibility(): void {
        const currentUrl = this.router.url;
        this.showSubmenu = !currentUrl.includes(this.excludedRoute);
        console.log(`Current URL: ${currentUrl}, Show Submenu: ${this.showSubmenu}`);
    }

    private setCurrentBreadcrumbOnReload(): void {
        let route = this.route;
        while (route.firstChild) {
            route = route.firstChild;
        }

        const titleKey = this.getTitleFromRoute(route);
        const translatedTitle = this.translate.instant(titleKey);
        this.pageTitle = translatedTitle;
        this.setBreadcrumb(translatedTitle);
    }

    /**
     * Aggiorna il breadcrumb basandosi sullo snapshot della route.
     * Questa funzione viene chiamata sia all'inizializzazione che ad ogni NavigationEnd.
     * @param routeSnapshot Lo snapshot della ActivatedRoute.
     */
    private updateBreadcrumbFromRouteSnapshot(routeSnapshot: any): void {
        const titleKey = routeSnapshot.data['title'] || '';
        const translatedTitle = this.translate.instant(titleKey);
        this.pageTitle = translatedTitle;
        this.setBreadcrumb(translatedTitle);
    }

    private getTitleFromRoute(route: ActivatedRoute): string {
        if (route.snapshot.data['title']) {
            return route.snapshot.data['title'];
        }
        return this.getTitleFromUrl(this.router.url);
    }

    private getTitleFromUrl(url: string): string {
        if (url.includes('/overview')) {
            return 'TABS.OVERVIEW';
        } else if (url.includes('/statistics')) {
            return 'TABS.STATISTICS';
        } else if (url.includes('/documents')) {
            return 'TABS.DOCUMENTS';
        } else if (url.includes('/conversations')) {
            return 'TABS.CONVERSATIONS';
        } else if (url.includes('/permissions')) {
            return 'TABS.PERMISSIONS';
        } else if (url.includes('/feedback')) {
            return 'TABS.FEEDBACK';
        } else if (url.includes('/settings')) {
            return 'TABS.SETTINGS';
        } else if (url.includes('/examples')) {
            return 'TABS.EXAMPLES';
        }
        return 'TABS.OVERVIEW';
    }

    private setBreadcrumb(label: string): void {
        // **IMPORTANTE**: Crea sempre un nuovo array per garantire che Angular rilevi il cambiamento.
        if (this.router.url.includes('/overview') || label === this.translate.instant('TABS.OVERVIEW')) {
            this.items = [];
        } else {
            this.items = [{ label: label }];
        }
    }

    private getTabLabel(tab: TabNames): string {
        switch (tab) {
            case TabNames.PANORAMICA:
                return this.translate.instant('TABS.OVERVIEW');
            case TabNames.STATISTICHE:
                return this.translate.instant('TABS.STATISTICS');
            case TabNames.DOCUMENTI:
                return this.translate.instant('TABS.DOCUMENTS');
            case TabNames.CONVERSAZIONI:
                return this.translate.instant('TABS.CONVERSATIONS');
            case TabNames.PERMESSI:
                return this.translate.instant('TABS.PERMISSIONS');
            case TabNames.IMPOSTAZIONI:
                return this.translate.instant('TABS.SETTINGS');
            default:
                return '';
        }
    }

    public toggleMenu(event: Event): void {
        this.hasSelectedTeaching$.pipe(
            take(1)
        ).subscribe(hasTeaching => {
            if (hasTeaching) {
                this.isMenuOpen = !this.isMenuOpen;
                console.log('Menu is now:', this.isMenuOpen ? 'Open' : 'Closed');
            } else {
                console.log('Cannot open menu: No teaching selected.');
            }
        });
    }

    public handleMenuCloseRequest(): void {
        this.isMenuOpen = false; 
        console.log('Menu closed by item click.');
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openLegendModal() {
            this.ref = this.dialogService.open(InsegnamentoLegendaModalComponent, {
                width: "30%",
                contentStyle: {overflow: "auto"},
                baseZIndex: 10000,
                maximizable: false,
            });
    
            this.ref.onClose.subscribe((result) => {
                if (result) {
                    console.log("Modale Legenda chiusa con risultato:", result);
                } else {
                    console.log("Modale Legenda chiusa senza risultato.");
                }
            });
        }
    
    openChangeModal() {
        this.ref = this.dialogService.open(CambioInsegnamentoComponent, {
            width: "1200px",
            contentStyle: {overflow: "auto"},
            baseZIndex: 10000,
            maximizable: false,
        });
    }

}